import React, { useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import { useInjection } from "@presentation/components/dependencyInjection";
import { TeamManagementService } from "@application/useCases/workspace/TeamManagementService";
import { extractProblemDetails } from "@presentation/common/ErrorHelpers";
import { UserPicker } from "@presentation/components/team/UserPicker";
import type { TeamDetail, UserSummary } from "@domain/schemas/workspace";
import { useWorkspaceSuccessNotification } from "@presentation/components/notifications/useWorkspaceSuccessNotification";

interface CreateTeamDialogProps {
  onClose: () => void;
  onSuccess: (created: TeamDetail) => void;
}

/**
 * Admin-only, with no manager-assignment step beyond the one required manager field: team
 * creation has no equivalent to project creation's editable initial-assignment set, since a
 * Team Manager can be assigned or changed afterward through the ordinary assign-role dialog.
 */
export const CreateTeamDialog: React.FC<CreateTeamDialogProps> = ({ onClose, onSuccess }) => {
  const teamManagementService = useInjection(TeamManagementService);
  const notifySuccess = useWorkspaceSuccessNotification();
  const [name, setName] = useState("");
  const [initialManager, setInitialManager] = useState<UserSummary | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const canSubmit = name.trim().length > 0 && Boolean(initialManager) && !isSubmitting;

  const handleSubmit = async () => {
    if (!initialManager) return;
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const created = await teamManagementService.createTeamAsync({ name: name.trim(), initialManagerUserId: initialManager.id });
      notifySuccess("team", "created");
      onSuccess(created);
      onClose();
    } catch (error) {
      setErrorMessage(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not create the team.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog title="Create team" onClose={onClose} width={420}>
      <div style={{ display: "grid", gap: 16 }}>
        <div>
          <label htmlFor="create-team-name" style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
            Team name
          </label>
          <Input id="create-team-name" style={{ width: "100%" }} value={name} onChange={(event) => setName(String(event.value ?? ""))} />
        </div>
        <div>
          <label htmlFor="create-team-manager" style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
            Initial manager
          </label>
          <UserPicker id="create-team-manager" value={initialManager} onChange={setInitialManager} />
        </div>
        {errorMessage && <div style={{ color: "#7f1d1d" }}>{errorMessage}</div>}
      </div>
      <DialogActionsBar>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button themeColor="primary" disabled={!canSubmit} onClick={handleSubmit}>
          Create team
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};
